import { useMemo } from "react";
import { useIsMainAppLoading, useIsInitialCollectionSyncCompleted } from "hooks";
import { useConversationContext } from "screens/thread/ConversationContext";

type LoadingReason = "app-initialization" | "content-preparation" | null;

interface LoadingState {
  isLoading: boolean;
  loadingReason: LoadingReason;
}

/**
 * Custom hook to manage loading states for the Home component
 * Consolidates all loading logic and provides performance optimization through memoization
 */
export const useHomeLoadingState = (): LoadingState => {
  const isMainAppLoading = useIsMainAppLoading();
  const isInitialCollectionSyncCompleted = useIsInitialCollectionSyncCompleted();
  const { filteredProjectsIds, isOnboardingModalOpen } = useConversationContext();

  return useMemo(() => {
    // Primary loading states - core app functionality not ready
    const isPrimaryLoading = isMainAppLoading || !isInitialCollectionSyncCompleted;

    // Secondary loading states - UI-specific conditions
    const isSecondaryLoading = !filteredProjectsIds || isOnboardingModalOpen;

    return {
      isLoading: isPrimaryLoading || isSecondaryLoading,
      loadingReason: isPrimaryLoading ? "app-initialization" : isSecondaryLoading ? "content-preparation" : null,
    };
  }, [isMainAppLoading, isInitialCollectionSyncCompleted, filteredProjectsIds, isOnboardingModalOpen]);
};
